import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import SparepartImportDeleteForm from "./SparepartImportDeleteForm";
import SparepartImportUpdateForm from "./SparepartImportUpdateForm";
import { getSparepartImports } from "../../../actions/sparepartImports";
import SparepartImportCreateForm from "./SparepartImportCreateForm";
import SparepartImportItem from "./SparepartImportItem";
import { Plus, Filter } from "react-feather";
import TableSpinner from "../../layout/TableSpinner";
import CustomPaginationWithManyPages from "../../layout/CustomPaginationWithManyPages";
import Select from 'react-select';
import { usePermission } from '../../../hooks/usePermission';
import { getSparepartOptions, getWarehouseOptions } from '../../../actions/spare_part';
import { getSupplierOptions } from '../../../actions/supplier';
import { getFirmOptionsForSparepartImports } from '../../../actions/user';
import FilterByDateRange from "../../layout/FilterByDateRange";
import FilterWithTrashed from "../../filters/FilterWithTrashed";
import FilterOnlyTrashed from "../../filters/FilterOnlyTrashed";
import { Button, Modal } from 'react-bootstrap';
import SearchForm from "../../layout/SearchForm";

// Create a DeletedSparepartImportsFilterModal component
const DeletedSparepartImportsFilterModal = ({ show, handleClose, getPageItems }) => {
    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>O'chirilgan ehtiyot qism importlar filtrlari</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <h6 className="mb-3">O'chirilgan ehtiyot qism importlar bilan ishlash:</h6>
                    <div className="mb-3">
                        <FilterWithTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Barcha importlarni o'chirilganlar bilan birga ko'rsatish
                        </small>
                    </div>
                    
                    <div className="mb-3">
                        <FilterOnlyTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Faqat o'chirilgan importlarni ko'rsatish
                        </small>
                    </div>
                </div>
                
                <div className="alert alert-info">
                    <small>
                        <strong>Eslatma:</strong> Bu filtrlar bir-birini bekor qiladi. Agar ikkala filtr ham yoqilgan bo'lsa, oxirgi tanlangan filtr ishlatiladi.
                    </small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Yopish
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const SparepartImports = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { userCan } = usePermission();
    const { sparepartImport } = useSelector(state => state);
    const { supplier } = useSelector(state => state);
    const { spare_part } = useSelector(state => state);
    const { user } = useSelector(state => state);
    const { 
        sparepartImports, 
        loading, 
        numberOfPages, 
        currentPage,
        total_sparepart_imports
    } = sparepartImport;
    const { suppliers_DATA } = supplier;
    const { spare_part_options } = spare_part;
    const { firm_options } = user;

    const [showCreate, setShowCreate] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedFirm, setSelectedFirm] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedSparepart, setSelectedSparepart] = useState(null);
    
    const [showDeletedFilters, setShowDeletedFilters] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Get URL parameters to check for trashed filters
    const params = new URLSearchParams(window.location.search);
    const withTrashed = params.get('with_trashed') === 'true';
    const onlyTrashed = params.get('only_trashed') === 'true';
    const hasDeletedFilters = withTrashed || onlyTrashed;

    // Initialize filters from URL parameters when options become available
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        
        // Initialize supplier filter from URL
        const supplierId = urlParams.get('supplier_id');
        if (supplierId && suppliers_DATA && suppliers_DATA.length > 0) {
            const supplier = suppliers_DATA.find(s => s.id.toString() === supplierId);
            if (supplier) {
                setSelectedSupplier({ value: supplier.id, label: supplier.name });
            }
        } else if (!supplierId) {
            setSelectedSupplier(null);
        }
    }, [location.search, suppliers_DATA]);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        
        // Initialize firm filter from URL
        const firmId = urlParams.get('firm_id');
        if (firmId && firm_options && firm_options.length > 0) {
            const firm = firm_options.find(f => f.id.toString() === firmId);
            if (firm) {
                setSelectedFirm({ value: firm.id, label: firm.name });
            }
        } else if (!firmId) {
            // If no firm is selected but there's only one firm available, auto-select it
            // This is helpful for managers who only manage one firm
            if (firm_options && firm_options.length === 1) {
                const onlyFirm = firm_options[0];
                setSelectedFirm({ value: onlyFirm.id, label: onlyFirm.name });
            } else {
                setSelectedFirm(null);
            }
        }
    }, [location.search, firm_options]);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        
        // Initialize warehouse filter from URL (only if we have warehouse options)
        const warehouseId = urlParams.get('warehouse_id');
        if (warehouseId && spare_part.warehouse_options && spare_part.warehouse_options.length > 0) {
            const warehouse = spare_part.warehouse_options.find(w => w.id.toString() === warehouseId);
            // Only set warehouse if it exists and belongs to the selected firm (or no firm is selected)
            if (warehouse && (!selectedFirm || warehouse.firm_id === selectedFirm.value)) {
                setSelectedWarehouse({ value: warehouse.id, label: warehouse.name });
            } else {
                // Warehouse doesn't belong to selected firm, clear it
                setSelectedWarehouse(null);
            }
        } else if (!warehouseId) {
            setSelectedWarehouse(null);
        }
    }, [location.search, spare_part.warehouse_options, selectedFirm]);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        
        // Initialize sparepart filter from URL
        const sparepartId = urlParams.get('sparepart_id');
        if (sparepartId && spare_part_options && spare_part_options.length > 0) {
            const sparepart = spare_part_options.find(sp => sp.id.toString() === sparepartId);
            if (sparepart) {
                setSelectedSparepart({ value: sparepart.id, label: sparepart.name });
            }
        } else if (!sparepartId) {
            setSelectedSparepart(null);
        }
    }, [location.search, spare_part_options]);

    const handleCreateClose = () => setShowCreate(false);
    const handleCreateShow = () => setShowCreate(true);
    
    const handleDeletedFiltersClose = () => setShowDeletedFilters(false);
    const handleDeletedFiltersShow = () => setShowDeletedFilters(true);
    
    const showLoader = () => setIsLoading(true);
    const hideLoader = () => setIsLoading(false);

    // Fetch initial data when component mounts
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const initialFilters = {};
        
        // Get filters from URL only (don't use getFilterParams to avoid state dependency)
        for (const [key, value] of urlParams.entries()) {
            initialFilters[key] = value;
        }
        
        // Add per_page parameter to ensure pagination works correctly
        initialFilters.per_page = 10;
        
        dispatch(getSparepartImports(1, initialFilters));
    }, [dispatch, location.search]);

    // Fetch options data when component mounts
    useEffect(() => {
        dispatch(getSupplierOptions());
        dispatch(getSparepartOptions());
        dispatch(getFirmOptionsForSparepartImports());
        dispatch(getWarehouseOptions({ type: 'part' }));
    }, [dispatch]);



    // Handle firm change effect for loading related data
    useEffect(() => {
        if (selectedFirm) {
            dispatch(getSparepartOptions({ firm_id: selectedFirm.value }));
            dispatch(getWarehouseOptions({ firm_id: selectedFirm.value, type: 'part' }));
        } else {
            dispatch(getSparepartOptions());
            dispatch(getWarehouseOptions({ type: 'part' }));
        }
    }, [selectedFirm, dispatch]);

    // Reset warehouse selection if it's no longer valid for the current firm
    useEffect(() => {
        if (selectedWarehouse && spare_part.warehouse_options && spare_part.warehouse_options.length > 0) {
            // Check if the selected warehouse is still valid for the current firm
            const isValidWarehouse = spare_part.warehouse_options.some(w => 
                w.id === selectedWarehouse.value && 
                (!selectedFirm || w.firm_id === selectedFirm.value)
            );
            
            if (!isValidWarehouse) {
                setSelectedWarehouse(null);
            }
        }
    }, [selectedFirm, spare_part.warehouse_options]);

    // Reset local loading state when Redux loading state changes
    useEffect(() => {
        if (!loading) {
            setIsLoading(false);
        }
    }, [loading]);



            if (!userCan('view sparepart imports')) {
        return <div>Siz ehtiyot qism importlarni ko'rish uchun ruxsatga ega emassiz.</div>;
    }

    const supplierOptions = suppliers_DATA ? suppliers_DATA.map(supplier => ({
        value: supplier.id,
        label: supplier.name
    })) : [];

    const firmOptions = firm_options ? firm_options.map(firm => ({
        value: firm.id,
        label: firm.name
    })) : [];

    // Filter warehouses based on selected firm
    const warehouseOptions = spare_part.warehouse_options ? 
        spare_part.warehouse_options
            .filter(warehouse => !selectedFirm || warehouse.firm_id === selectedFirm.value)
            .map(warehouse => ({
                value: warehouse.id,
                label: warehouse.name
            })) : [];

    const sparepartOptions = spare_part_options ? spare_part_options.map(sparepart => ({
        value: sparepart.id,
        label: sparepart.name
    })) : [];

    const handleSupplierChange = useCallback((selected) => {
        setSelectedSupplier(selected);
        
        const params = new URLSearchParams(location.search);
        const filters = {};
        
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }
        
        if (selected) {
            filters.supplier_id = selected.value;
        } else {
            delete filters.supplier_id;
        }
        filters.per_page = 10;
        
        // Update URL parameters
        const url = new URL(window.location);
        if (selected) {
            url.searchParams.set('supplier_id', selected.value);
        } else {
            url.searchParams.delete('supplier_id');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        dispatch(getSparepartImports(1, filters));
    }, [dispatch, location.search]);

    const handleFirmChange = useCallback((selected) => {
        setSelectedFirm(selected);
        setSelectedWarehouse(null); // Reset warehouse when firm changes
        
        const params = new URLSearchParams(location.search);
        const filters = {};
        
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }
        
        if (selected) {
            filters.firm_id = selected.value;
        } else {
            delete filters.firm_id;
        }
        delete filters.warehouse_id; // Remove warehouse filter when firm changes
        filters.per_page = 10;
        
        // Update URL parameters
        const url = new URL(window.location);
        if (selected) {
            url.searchParams.set('firm_id', selected.value);
        } else {
            url.searchParams.delete('firm_id');
        }
        url.searchParams.delete('warehouse_id'); // Remove warehouse from URL when firm changes
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        dispatch(getSparepartImports(1, filters));
    }, [dispatch, location.search]);

    const handleWarehouseChange = useCallback((selected) => {
        setSelectedWarehouse(selected);
        
        const params = new URLSearchParams(location.search);
        const filters = {};
        
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }
        
        if (selected) {
            filters.warehouse_id = selected.value;
        } else {
            delete filters.warehouse_id;
        }
        filters.per_page = 10;
        
        // Update URL parameters
        const url = new URL(window.location);
        if (selected) {
            url.searchParams.set('warehouse_id', selected.value);
        } else {
            url.searchParams.delete('warehouse_id');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        dispatch(getSparepartImports(1, filters));
    }, [dispatch, location.search]);

    const handleSparepartChange = useCallback((selected) => {
        setSelectedSparepart(selected);
        
        const params = new URLSearchParams(location.search);
        const filters = {};
        
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }
        
        if (selected) {
            filters.sparepart_id = selected.value;
        } else {
            delete filters.sparepart_id;
        }
        filters.per_page = 10;
        
        // Update URL parameters
        const url = new URL(window.location);
        if (selected) {
            url.searchParams.set('sparepart_id', selected.value);
        } else {
            url.searchParams.delete('sparepart_id');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        dispatch(getSparepartImports(1, filters));
    }, [dispatch, location.search]);

    // Check if we have pagination data and total items
    const hasPaginationData = numberOfPages !== undefined && currentPage !== undefined;
    const hasItems = sparepartImports && sparepartImports.length > 0;
    const shouldShowPagination = hasPaginationData && (numberOfPages > 1 || total_sparepart_imports > 10);

    return (
        <Fragment>
            <div className="container-fluid p-0">
                {/* Header */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <h4 className="mb-0">
                            Ehtiyot Qism Importlar
                            {total_sparepart_imports !== undefined && (
                                <span className="badge bg-secondary ms-2">{total_sparepart_imports}</span>
                            )}
                        </h4>
                        <small className="text-muted">
                            Ehtiyot qismlar import tarixi va boshqaruvi
                        </small>
                    </div>
                    <div className="col-md-6 text-end">
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="me-2"
                            onClick={handleDeletedFiltersShow}
                        >
                            <Filter size={16} className="me-1" />
                            O'chirilganlar
                            {hasDeletedFilters && <span className="badge bg-warning ms-1">1</span>}
                        </Button>
                        
                        {userCan('create sparepart imports') && (
                            <Button 
                                variant="primary" 
                                size="sm"
                                onClick={handleCreateShow}
                            >
                                <Plus size={16} className="me-1" />
                                Yangi Import
                            </Button>
                        )}
                    </div>
                </div>

                {/* Filters */}
                <div className="row mb-3">
                    <div className="col-md-3">
                        <Select
                            value={selectedFirm}
                            onChange={handleFirmChange}
                            options={firmOptions}
                            placeholder={firmOptions.length === 0 ? "Firmalar yuklanmoqda..." : "Barcha firmalar"}
                            isClearable
                            isLoading={!firm_options}
                        />
                    </div>
                    <div className="col-md-3">
                        <Select
                            value={selectedWarehouse}
                            onChange={handleWarehouseChange}
                            options={warehouseOptions}
                            placeholder={
                                selectedFirm 
                                    ? warehouseOptions.length === 0 
                                        ? "Bu firma uchun omborlar yo'q" 
                                        : "Omborni tanlang"
                                    : "Avval firmani tanlang"
                            }
                            isClearable
                            isDisabled={!selectedFirm}
                        />
                    </div>
                    <div className="col-md-3">
                        <Select
                            value={selectedSupplier}
                            onChange={handleSupplierChange}
                            options={supplierOptions}
                            placeholder="Barcha ta'minotchilar"
                            isClearable
                        />
                    </div>
                    <div className="col-md-3">
                        <Select
                            value={selectedSparepart}
                            onChange={handleSparepartChange}
                            options={sparepartOptions}
                            placeholder="Barcha ehtiyot qismlar"
                            isClearable
                        />
                    </div>
                </div>

                {/* Search and Date Range Filter */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <SearchForm 
                            getPageItems={getSparepartImports}
                            placeholder="Import raqami yoki izoh..."
                        />
                    </div>
                    <div className="col-md-6">
                        <FilterByDateRange 
                            getPageItems={getSparepartImports}
                            showLoader={showLoader}
                            hideLoader={hideLoader}
                        />
                    </div>
                </div>

                {/* Imports Table */}
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body p-0">
                                {(loading || isLoading) && <TableSpinner />}
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col">#</th>
                                                <th scope="col">Import raqami</th>
                                                <th scope="col">Ombor</th>
                                                <th scope="col">Ehtiyot qism</th>
                                                <th scope="col">Ta'minotchi</th>
                                                <th scope="col">Transport raqami</th>
                                                <th scope="col">Jami summa</th>
                                                <th scope="col">Sana</th>
                                                <th scope="col">Amallar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hasItems ? (
                                                sparepartImports.map((item, index) => (
                                                    <SparepartImportItem
                                                        key={item.id}
                                                        sparepartImport={item}
                                                        number={(currentPage - 1) * 10 + index + 1}
                                                    />
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {loading || isLoading ? 'Ma\'lumotlar yuklanmoqda...' : 'Ehtiyot qism importlar topilmadi'}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pagination */}
                {shouldShowPagination && (
                    <div className="row mt-3">
                        <div className="col-12">
                            <CustomPaginationWithManyPages
                                numberOfPages={numberOfPages}
                                getPageItems={getSparepartImports}
                                current_page={currentPage}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {userCan('create sparepart imports') && (
                <SparepartImportCreateForm
                    show={showCreate}
                    handleClose={handleCreateClose}
                />
            )}

            {userCan('edit sparepart imports') && (
                <SparepartImportUpdateForm />
            )}

            {userCan('delete sparepart imports') && (
                <SparepartImportDeleteForm />
            )}

            <DeletedSparepartImportsFilterModal
                show={showDeletedFilters}
                handleClose={handleDeletedFiltersClose}
                getPageItems={getSparepartImports}
            />
        </Fragment>
    );
};

export default SparepartImports; 